import { Autocomplete, Avatar, Chip, TextField } from "@mui/material";

import { useState, FunctionComponent, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { LinkedinPersonSuggestionItem } from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";
import { SvgIcon } from "@/components/Icon/SvgIcon";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { CreateLiSn, IncludedOrExcluded, LiSnFilter } from "../schema/liSn";

interface LiSnAutocompleteProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleChange: (data: { value: any; name: keyof CreateLiSn }[]) => Promise<void>;
	organizationSuggestions?: LinkedinPersonSuggestionItem[];
	setSearchValue: (value: string) => void;
	searchValue: string;
	value?: LiSnFilter[];
	item: keyof CreateLiSn;
	loading?: boolean;
	ownValue?: boolean;
}

export const LiSnAutocomplete: FunctionComponent<LiSnAutocompleteProps> = ({
	handleChange,
	organizationSuggestions,
	setSearchValue,
	searchValue,
	value = [],
	item,
	loading,
	ownValue = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liSn.${key}`);
	const [showDropdown, setShowDropdown] = useState(false);

	const [options, setOptions] = useState<
		{ id: string; title: string; includedOrExcluded: IncludedOrExcluded; image: string }[]
	>([]);

	useEffect(() => {
		setOptions(
			organizationSuggestions?.map((item) => {
				return {
					id: item.urn,
					title: item.title,
					includedOrExcluded: IncludedOrExcluded.INCLUDED,
					image: item.image || "",
				};
			}) || []
		);
	}, [organizationSuggestions, searchValue]);

	return (
		<Autocomplete
			defaultValue={[]}
			filterOptions={(options) => {
				return options;
			}}
			getOptionLabel={(option) => option.title}
			id={item}
			inputValue={searchValue}
			loading={loading}
			loadingText={"Searching..."}
			multiple={true}
			noOptionsText={searchValue.length > 0 ? "Typing..." : "Start typing to search"}
			options={
				ownValue
					? [
							{
								id: searchValue,
								title: searchValue,
								includedOrExcluded: IncludedOrExcluded.INCLUDED,
								image: "",
							},
							...options,
						]
					: options
			}
			popupIcon={
				<SvgIcon className="w-[20px] h-[20px]" svgIcon={showDropdown ? ChevronUp : ChevronDown} />
			}
			renderInput={(params) => {
				return (
					<TextField
						sx={{
							width: "100%",
							minHeight: 48,
							marginBottom: 1,
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								borderRadius: 1.5,
								paddingTop: "0px",
								paddingBottom: "0px !important",
								paddingLeft: 1.5,
								"&.Mui-focused": {
									borderWidth: 1,
									boxShadow: "none",
								},
							},
						}}
						{...params}
						placeholder={ts(item)}
					/>
				);
			}}
			renderTags={(value, getTagProps) =>
				value.map((option, index) => (
					<Chip
						label={option.title}
						{...getTagProps({ index })}
						key={`${option.id}-${option.includedOrExcluded}`}
						avatar={option?.image ? <Avatar alt={option.title} src={option.image} /> : undefined}
						color={option.includedOrExcluded === IncludedOrExcluded.UNSURE ? "error" : "primary"}
						onClick={(e) => {
							// Now you have access to "option" (the chip's data)
							option.includedOrExcluded =
								option.includedOrExcluded === IncludedOrExcluded.INCLUDED
									? IncludedOrExcluded.UNSURE
									: IncludedOrExcluded.INCLUDED;

							const uniqueValues = value.map((item) => {
								if (item.id === option.id) {
									return {
										id: item.id,
										name: item.title,
										includedOrExcluded: option.includedOrExcluded,
										image: item.image || "",
									};
								}

								return {
									id: item.id,
									name: item.title,
									includedOrExcluded: item.includedOrExcluded,
									image: item.image || "",
								};
							});

							handleChange([
								{
									value: uniqueValues,
									name: item,
								},
							]);

							e.stopPropagation();
						}}
					/>
				))
			}
			size="medium"
			sx={{
				width: "100%",
			}}
			value={value?.map((item) => {
				return {
					id: item.id,
					title: item.name,
					includedOrExcluded: item.includedOrExcluded,
					image: item.image || "",
				};
			})}
			onChange={(event, cValue) => {
				const uniqueValues = cValue?.filter(
					(item, index, self) => index === self.findIndex((t) => t.id === item.id)
				);

				handleChange([
					{
						value: uniqueValues.map((item) => {
							return {
								id: item.id,
								name: item.title,
								includedOrExcluded: item.includedOrExcluded,
								image: item.image || "",
							};
						}),
						name: item,
					},
				]);
			}}
			onClose={() => {
				setShowDropdown(false);
			}}
			onInputChange={(event, newInputValue) => {
				if (event?.type === "change") {
					setOptions([]);
				}

				if (event) {
					setSearchValue(newInputValue);
				}

				if (!showDropdown) {
					setShowDropdown(true);
				}
			}}
			onOpen={() => {
				setShowDropdown(true);
			}}
		/>
	);
};
